import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';

export default function About() {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About Us - Allgo Digital Sdn Bhd",
    "description": "Learn about Allgo Digital's mission, leadership team, career opportunities, and awards in enterprise software development solutions.",
    "url": `${import.meta.env.VITE_SITE_URL}/about`,
    "mainEntity": {
      "@type": "Organization",
      "name": "Allgo Digital Sdn Bhd",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "E-1-6, Kuchai Business Park Jalan Kuchai Lama, Off, Jalan 1/127, Kuchai Lama",
        "addressLocality": "Kuala Lumpur",
        "postalCode": "58200",
        "addressCountry": "MY"
      },
      "email": "info@allgo-digital.com"
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="About Us - Allgo Digital Sdn Bhd | Enterprise Software Solutions"
        description="Learn about Allgo Digital's mission, leadership team, career opportunities, and awards in enterprise software development solutions."
        keywords="about allgo digital, company overview, leadership team, careers, awards, enterprise software"
        canonical="/about"
        structuredData={structuredData}
      />

      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/feaac7b19917aec559683ef9ad3d4cd6/f01248d0a1f191b5d9d27b9480b2a552.jpeg" 
                alt="Allgo Digital Logo" 
                className="h-16 w-auto"
              />
            </div>
            <div className="flex items-center space-x-8">
              <a href="/platforms" className="text-gray-600 hover:text-gray-900 font-medium">Platforms</a>
              <a href="/features" className="text-gray-600 hover:text-gray-900 font-medium">Features</a>
              <a href="/marketplace" className="text-gray-600 hover:text-gray-900 font-medium">Marketplace</a>
              <a href="/partners" className="text-gray-600 hover:text-gray-900 font-medium">Partners</a>
              <a href="/resources" className="text-gray-600 hover:text-gray-900 font-medium">Resources</a>
              <a href="/support" className="text-gray-600 hover:text-gray-900 font-medium">Support</a>
              <Link to="/contact" className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer">
                Schedule Demo
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Back Button */}
      <div className="bg-gray-50 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-end">
          <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium cursor-pointer">
            <i className="ri-arrow-left-line mr-2"></i>
            Back to Home
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-white mb-6">
            About Allgo Digital
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Pioneering the future of enterprise software development with innovative low-code, data integration, and business intelligence solutions.
          </p>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Company Overview
              </h2>
              <div className="space-y-6 text-lg text-gray-600">
                <p>
                  Founded with a vision to revolutionize enterprise software development, Allgo Digital Sdn Bhd has emerged as a leading provider of integrated platform solutions that empower businesses to accelerate their digital transformation journey.
                </p>
                <p>
                  Our comprehensive suite of platforms - Codee, Fusion, and BI Platform - represents years of innovation and expertise in low-code development, data integration, and business intelligence. We believe that technology should be accessible, powerful, and transformative for organizations of all sizes.
                </p>
                <p>
                  Based in Kuala Lumpur, Malaysia, we serve clients across the Asia-Pacific region and beyond, helping them build better applications faster, integrate their data seamlessly, and make informed decisions through actionable insights.
                </p>
                <div className="grid grid-cols-3 gap-8 mt-8">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600 mb-2">500+</div>
                    <div className="text-sm text-gray-600">Enterprise Clients</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-purple-600 mb-2">10+</div>
                    <div className="text-sm text-gray-600">Years Experience</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600 mb-2">99.9%</div>
                    <div className="text-sm text-gray-600">Platform Uptime</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://public.readdy.ai/ai/img_res/9d62a8af6ac727bc86f0a3be29526c26.jpg"
                alt="Allgo Digital Office Building"
                className="rounded-xl shadow-lg object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Leadership Team
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Meet the visionary leaders driving innovation and excellence at Allgo Digital.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* CEO */}
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20Asian%20business%20executive%20CEO%20in%20modern%20office%20setting%2C%20confident%20leadership%20pose%2C%20wearing%20business%20suit%2C%20corporate%20headshot%20style%20with%20clean%20background%20and%20professional%20lighting&width=300&height=300&seq=ceo-portrait&orientation=squarish"
                alt="CEO Portrait"
                className="w-32 h-32 rounded-full mx-auto mb-6 object-cover"
              />
              <h3 className="text-xl font-bold text-gray-900 mb-2">David Lim</h3>
              <p className="text-blue-600 font-medium mb-4">Chief Executive Officer</p>
              <p className="text-gray-600 text-sm">
                With over 15 years of experience in enterprise software, David leads Allgo Digital's strategic vision and growth initiatives. Previously held senior positions at major technology companies across Southeast Asia.
              </p>
            </div>

            {/* CTO */}
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20Asian%20technology%20executive%20CTO%20in%20modern%20tech%20environment%2C%20innovative%20leadership%20style%2C%20wearing%20smart%20casual%20attire%2C%20corporate%20headshot%20with%20technology%20background%20elements&width=300&height=300&seq=cto-portrait&orientation=squarish"
                alt="CTO Portrait"
                className="w-32 h-32 rounded-full mx-auto mb-6 object-cover"
              />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Sarah Chen</h3>
              <p className="text-purple-600 font-medium mb-4">Chief Technology Officer</p>
              <p className="text-gray-600 text-sm">
                Sarah drives the technical innovation behind our platform suite. With a PhD in Computer Science and expertise in AI and data analytics, she ensures our solutions remain at the cutting edge of technology.
              </p>
            </div>

            {/* VP Sales */}
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20Asian%20business%20development%20executive%20in%20client-facing%20role%2C%20approachable%20leadership%20style%2C%20wearing%20professional%20business%20attire%2C%20corporate%20headshot%20with%20warm%20lighting%20and%20confident%20expression&width=300&height=300&seq=vp-sales-portrait&orientation=squarish"
                alt="VP Sales Portrait"
                className="w-32 h-32 rounded-full mx-auto mb-6 object-cover"
              />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Michael Wong</h3>
              <p className="text-green-600 font-medium mb-4">VP of Sales & Partnerships</p>
              <p className="text-gray-600 text-sm">
                Michael leads our global sales and partnership initiatives, building strategic relationships with enterprise clients and technology partners. His extensive network spans across multiple industries and regions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Career Opportunities */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Career Opportunities
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Join our dynamic team and help shape the future of enterprise software development.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Open Positions */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Open Positions</h3>
              <div className="space-y-4">
                <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-600">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="text-lg font-semibold text-gray-900">Senior Full Stack Developer</h4>
                    <span className="text-sm text-blue-600 bg-blue-100 px-3 py-1 rounded-full">Remote</span>
                  </div>
                  <p className="text-gray-600 mb-3">Join our platform development team to build next-generation low-code solutions.</p>
                  <div className="flex items-center text-sm text-gray-500">
                    <i className="ri-map-pin-line mr-2"></i>
                    <span>Kuala Lumpur / Remote</span>
                    <i className="ri-time-line ml-4 mr-2"></i>
                    <span>Full-time</span>
                  </div>
                </div>

                <div className="bg-purple-50 p-6 rounded-lg border-l-4 border-purple-600">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="text-lg font-semibold text-gray-900">Data Engineer</h4>
                    <span className="text-sm text-purple-600 bg-purple-100 px-3 py-1 rounded-full">Hybrid</span>
                  </div>
                  <p className="text-gray-600 mb-3">Design and implement data integration solutions for our Fusion platform.</p>
                  <div className="flex items-center text-sm text-gray-500">
                    <i className="ri-map-pin-line mr-2"></i>
                    <span>Kuala Lumpur</span>
                    <i className="ri-time-line ml-4 mr-2"></i>
                    <span>Full-time</span>
                  </div>
                </div>

                <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-600">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="text-lg font-semibold text-gray-900">UX/UI Designer</h4>
                    <span className="text-sm text-green-600 bg-green-100 px-3 py-1 rounded-full">On-site</span>
                  </div>
                  <p className="text-gray-600 mb-3">Create intuitive user experiences for our business intelligence platform.</p>
                  <div className="flex items-center text-sm text-gray-500">
                    <i className="ri-map-pin-line mr-2"></i>
                    <span>Kuala Lumpur</span>
                    <i className="ri-time-line ml-4 mr-2"></i>
                    <span>Full-time</span>
                  </div>
                </div>

                <div className="bg-orange-50 p-6 rounded-lg border-l-4 border-orange-600">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="text-lg font-semibold text-gray-900">DevOps Engineer</h4>
                    <span className="text-sm text-orange-600 bg-orange-1 0px px-3 py-1 rounded-full">Remote</span>
                  </div>
                  <p className="text-gray-600 mb-3">Manage cloud infrastructure and deployment pipelines for our platform suite.</p>
                  <div className="flex items-center text-sm text-gray-500">
                    <i className="ri-map-pin-line mr-2"></i>
                    <span>Kuala Lumpur / Remote</span>
                    <i className="ri-time-line ml-4 mr-2"></i>
                    <span>Full-time</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Benefits & Culture */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Join Us?</h3>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-heart-line text-xl text-blue-600"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Comprehensive Benefits</h4>
                    <p className="text-gray-600">Health insurance, dental coverage, annual leave, and professional development budget.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-rocket-line text-xl text-purple-600"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Innovation Culture</h4>
                    <p className="text-gray-600">Work on cutting-edge technology with a team that values creativity and innovation.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-global-line text-xl text-green-600"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Global Impact</h4>
                    <p className="text-gray-600">Your work will impact enterprises across the Asia-Pacific region and beyond.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-team-line text-xl text-orange-600"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Collaborative Environment</h4>
                    <p className="text-gray-600">Work with talented professionals in a supportive and inclusive environment.</p>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <Link to="/contact" className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors inline-block cursor-pointer">
                  Apply Now
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Awards & Recognition */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Awards & Recognition
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our commitment to excellence has been recognized by industry leaders and organizations.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-trophy-line text-2xl text-yellow-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Best Enterprise Platform</h3>
              <p className="text-sm text-gray-600 mb-2">Asia Tech Awards 2023</p>
              <p className="text-xs text-gray-500">Recognized for innovation in low-code development</p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-star-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Top Data Integration Solution</h3>
              <p className="text-sm text-gray-600 mb-2">Data Management Excellence 2023</p>
              <p className="text-xs text-gray-500">Leading data integration platform in APAC</p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-medal-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Innovation Excellence</h3>
              <p className="text-sm text-gray-600 mb-2">Malaysia Tech Innovation 2 2022</p>
              <p className="text-xs text-gray-500">Outstanding contribution to enterprise technology</p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-award-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Customer Choice Award</h3>
              <p className="text-sm text-gray-600 mb-2">Enterprise Software Review 2022</p>
              <p className="text-xs text-gray-500">Highest customer satisfaction rating</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Join hundreds of enterprises who trust Allgo Digital to accelerate their digital transformation journey.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
              Schedule Demo
            </Link>
            <Link to="/partners" className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">
              Become a Partner
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <img 
                src="https://static.readdy.ai/image/feaac7b19917aec559683ef9ad3d4cd6/f01248d0a1f191b5d9d27b9480b2a552.jpeg" 
                alt="Allgo Digital Logo" 
                className="h-12 w-auto mb-4 filter brightness-0 invert"
              />
              <p className="text-gray-400">
                Transforming enterprise software development with integrated low-code, data flow, and BI solutions.
              </p>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Platforms</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/platforms" className="hover:text-white cursor-pointer">Codee Platform</Link></li>
                <li><Link to="/platforms" className="hover:text-white cursor-pointer">Fusion Platform</Link></li>
                <li><Link to="/platforms" className="hover:text-white cursor-pointer">BI Platform</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/resources" className="hover:text-white cursor-pointer">Resources</Link></li>
                <li><Link to="/features" className="hover:text-white cursor-pointer">Features</Link></li>
                <li><Link to="/partners" className="hover:text-white cursor-pointer">Partner Program</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400">
                <li>info@allgo-digital.com</li>
                <li>+60 3-7931 8888</li>
                <li>Enterprise Solutions</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Allgo Digital Sdn Bhd. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
